/**
 * Typed API client for Workgroups / Projects — Epic 217.
 *
 * Named collaboration groups that may also be projects (start/finish + optional
 * scrum). Members carry a role. Mirrors internal/workgroups.
 */

import { apiClient } from '@/lib/apiClient';

export type MemberRole = 'owner' | 'admin' | 'member' | 'guest';

export interface Workgroup {
  id: string;
  name: string;
  description: string | null;
  isProject: boolean;
  projectStart: string | null;
  projectFinish: string | null;
  isScrum: boolean;
  ownerUserId: string | null;
  createdAt: string;
}

export interface WorkgroupMember {
  userId: string;
  role: MemberRole;
  joinedAt: string;
}

export async function listWorkgroups(): Promise<Workgroup[]> {
  const { data } = await apiClient.get<Workgroup[]>('/workgroups');
  return data;
}

export interface CreateWorkgroupRequest {
  name: string;
  description?: string;
  isProject?: boolean;
  projectStart?: string;
  projectFinish?: string;
  isScrum?: boolean;
  scrumMasterId?: string;
  ownerUserId?: string;
}

export async function createWorkgroup(req: CreateWorkgroupRequest): Promise<{ id: string }> {
  const { data } = await apiClient.post<{ id: string }>('/workgroups', req);
  return data;
}

export async function listMembers(workgroupId: string): Promise<WorkgroupMember[]> {
  const { data } = await apiClient.get<WorkgroupMember[]>(`/workgroups/${workgroupId}/members`);
  return data;
}

// addMember / removeMember use the GUARDED endpoints (Epic 226 "utilizable"):
// the backend enforces that the actor is a workgroup owner/admin, closing the
// privilege-escalation gap where any member could mutate membership.

export async function addMember(
  workgroupId: string,
  userId: string,
  role: MemberRole = 'member',
): Promise<{ id: string }> {
  const { data } = await apiClient.post<{ id: string }>(
    `/workgroups-guarded/${workgroupId}/members`,
    { userId, role },
  );
  return data;
}

export async function removeMember(workgroupId: string, userId: string): Promise<void> {
  await apiClient.delete(`/workgroups-guarded/${workgroupId}/members/${userId}`);
}
